import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { WorkflowContent } from "../../../api/content/authoring/processContentDecoders";
import { classNames } from "../../../ui/styles/classNames";
import { workflowGraphDiff, type DiffStatus, type GraphDiffNode } from "../../workflows/model/workflowChanges";
import { PlacedUseName } from "../../workflows/edit/WorkflowChangeList";
import ChangeRow, { ChangeMark } from "../../../ui/content/ChangeRow";
import { changeToneClass, type ChangeKind } from "../../../ui/styles/changeStyles";

const KIND: Readonly<Record<DiffStatus, ChangeKind>> = { added: "added", removed: "removed", changed: "changed", same: "unchanged" };

/**
 * A Workflow change as a small graph (AU-8): the inputs above, the pipeline in order with
 * arrows, each step marked added (green), removed (red, struck through) or changed
 * (outlined); then the bindings that were added or removed. Written as lists, so a screen
 * reader reads the same.
 */
export default function WorkflowGraphDiff({ before, after }: { before: WorkflowContent; after: WorkflowContent }) {
  const { t } = useTranslation();
  const diff = useMemo(() => workflowGraphDiff(before, after), [before, after]);
  const inputs = diff.nodes.filter((node) => node.role === "input");
  const main = diff.nodes.filter((node) => node.role === "main");
  const changedEdges = diff.edges.filter((edge) => edge.status !== "same");
  return (
    <div className="space-y-2 text-xs">
      {inputs.length === 0 ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="app-label">{t("reviews.graphDiff.inputs")}</span>
          <ul className="flex flex-wrap gap-2">
            {inputs.map((node) => <DiffNode key={node.key} node={node} />)}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="app-label">{t("reviews.graphDiff.pipeline")}</span>
        <ol className="flex flex-wrap items-center gap-1">
          {main.map((node, index) => (
            <Fragment key={node.key}>
              {index === 0 ? null : <li aria-hidden={true} className="app-text-muted">→</li>}
              <DiffNode node={node} />
            </Fragment>
          ))}
        </ol>
      </div>
      {changedEdges.length === 0 ? null : (
        <ul className="space-y-0.5" aria-label={t("reviews.graphDiff.bindings")}>
          {changedEdges.map((edge) => (
            <ChangeRow key={`${edge.status} ${edge.key}.${edge.slot} ${edge.from.from_step}.${edge.from.part}`} kind={KIND[edge.status]} className={classNames("font-mono", edge.status === "removed" && "line-through")}>
              {t("reviews.graphDiff.binding", { target: `${edge.key}.${edge.slot}`, source: `${edge.from.from_step}.${edge.from.part}` })}
            </ChangeRow>
          ))}
        </ul>
      )}
    </div>
  );
}

function DiffNode({ node }: { node: GraphDiffNode }) {
  const { t } = useTranslation();
  return (
    <li className={classNames("rounded border px-2 py-1", node.status === "same" ? "app-surface-muted" : changeToneClass(KIND[node.status]), node.status === "removed" && "line-through", node.status === "changed" ? "border-current" : "border-transparent")}>
      <ChangeMark kind={KIND[node.status]} />
      <PlacedUseName use={node.use} link={false} />
      {node.use.kind === "step" ? null : <span className="app-text-muted"> · {t(node.use.kind === "workflow" ? "reviews.graphDiff.subWorkflow" : "reviews.graphDiff.abstractStep")}</span>}
    </li>
  );
}
